import { useState, useEffect } from 'react';
import { KeyedMutator } from 'swr';
import { Box } from '@mui/system';
import { NavTabs } from '@shared/tabs/NavTabs';
import { queryParams } from '@shared/commonFunctions';
import { ILeadData } from '../Header/interfaces';
import { Contact } from './components/Contact/Contact';
import { Leads } from './components/Leads';
import { Deals } from './components/Deals';
import { ContactFiles } from './components/ContactFiles/ContactFiles';
import { NContact } from './components/Contact/validation';
import { Assets } from './components/Assets';
import { IDeal, IDealSummary, OtherIncome } from './components/Deals/interfaces';
import { IContactDetails } from './components/Contact/components/OptOutModal/interfaces';
import { ILead } from './components/Leads/types/interfaces/interfaces';
import { IAsset } from './components/Assets/interfaces';

interface IContactManagerProps {
  assets: IAsset[];
  selectLeadId: (leadId: string) => void;
  selectedLeadId: string;
  displayAdditionalInfo: boolean;
  reloadContactAlerts: () => void;
  reloadLeadData: KeyedMutator<ILeadData>;
  reloadContact: KeyedMutator<NContact.Entity>;
  reloadLeads: KeyedMutator<ILead[]>;
  reloadDeals: KeyedMutator<IDealSummary[]>;
  reloadAssets: KeyedMutator<IAsset[]>;
  contactData?: NContact.Entity;
  dealSummary: IDealSummary[] | [];
  deal?: IDeal;
  validatingDeals: boolean;
  validatingAssets: boolean;
  dealsSummaryError?: Error;
}

export const ContactManagerTabs = ({
  assets,
  selectLeadId,
  selectedLeadId,
  displayAdditionalInfo,
  reloadContactAlerts,
  reloadLeadData,
  reloadLeads,
  reloadDeals,
  reloadAssets,
  contactData,
  reloadContact,
  deal,
  dealSummary,
  validatingDeals,
  validatingAssets,
  dealsSummaryError,
}: IContactManagerProps) => {
  const params = queryParams();
  const [currentAddress, setCurrentAddress] = useState<NContact.Address>();
  const [contactDetails, setContactDetails] = useState<IContactDetails>({});

  useEffect(() => {
    setCurrentAddress(contactData?.addresses?.find((address) => address.isCurrent));
    setContactDetails({
      firstName: contactData?.firstName ?? '',
      lastName: contactData?.lastName ?? '',
      email: contactData?.email ?? '',
      cellPhone: contactData?.cellPhone ?? '',
      homePhone : contactData?.homePhone ?? '',
      workPhone : contactData?.workPhone ?? '',
    });
  }, [contactData]);

  useEffect(() => {
    setContactDetails({
      ...contactDetails,
      state: currentAddress?.state ?? ''
    });
  }, [currentAddress]);  

  const tabs = [
    {
      name: 'Contact',
      component: (
        <Contact
          contactData={contactData as NContact.Entity}
          paramId={params.id}
          displayAdditionalInfo={displayAdditionalInfo}
          reloadContact={reloadContact}
        />
      ),
    },
    {
      name: 'Assets',
      component: (
        <Assets
          assets={assets}
          coApplicants={contactData?.coApplicants}
          reloadAssets={reloadAssets}
          validatingAssets={validatingAssets}
          deals={dealSummary}
          reloadHeaderData={reloadLeadData}
          onSave={reloadContactAlerts}
        />
      ),
    },
    {
      name: 'Leads',
      component: (
        <Leads
          selectLeadId={selectLeadId}
          selectedLeadId={selectedLeadId}
          contactDetails={contactDetails}
          reloadLeads={reloadLeads}
          reloadContactAlerts={reloadContactAlerts}
        />
      ),
    },
    {
      name: 'Deals',
      component: (
        <Deals
          deals={dealSummary}
          selectLeadId={selectLeadId}
          userCurrentAddress={currentAddress}
          otherIncomes={contactData?.otherIncomes as OtherIncome[] | undefined}
          reloadLeadData={reloadLeadData}
          reloadDeals={reloadDeals}
          validatingDeals={validatingDeals}
          dealsSummaryError={dealsSummaryError}
          reloadContactAlerts={reloadContactAlerts}
        />
      ),
    },
    {
      name: 'Files',
      component: (
        <ContactFiles
          hideDownladAllBtn
          deal={deal}
          dealSummary={dealSummary}
        />
      ),
    },
  ];

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <NavTabs
        data={tabs}
        loading={false}
      />
    </Box>
  );
};
